// alternative-components/CFOCopilot/OpportunityInfoCard.tsx
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useCountry } from "@/app/services/context/CountryContext";
import { gxDashboardAccountsBalanceApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
import InfoCard from "./InfoCard";
import type { DashboardAccount } from "@/app/types/alterNativeDashboardLayout";

interface OpportunityInfoCardProps {
  title?: string;
}

export interface OpportunityAccountRaw {
  accountName: string;
  accountBankName: string;
  accountIbanNo: string | null;
  availableBalance: number;
  accountCurrencyCode: string;
  opportunityPercentage: number | null;
}

interface OpportunityAccountItem {
  accountName: string;
  bankName: string;
  maskedAccountNo: string;
  balance: number;
  currencyCode: string;
  opportunityPercentage: number;
}

const maskAccountRef = (ibanNo: string | null): string =>
  ibanNo ? `****${ibanNo.slice(-4)}` : "****";

const mapOpportunityAccounts = (
  raw: OpportunityAccountRaw[] | undefined,
): OpportunityAccountItem[] => {
  if (!raw || raw.length === 0) return [];

  const totalBalance = raw.reduce(
    (sum, item) => sum + (item.availableBalance ?? 0),
    0,
  );

  return raw.map((item) => {
    const computedShare =
      totalBalance > 0
        ? Math.round(((item.availableBalance ?? 0) / totalBalance) * 100)
        : 0;

    return {
      accountName: item.accountName,
      bankName: item.accountBankName,
      maskedAccountNo: maskAccountRef(item.accountIbanNo),
      balance: item.availableBalance ?? 0,
      currencyCode: item.accountCurrencyCode || "AED",
      opportunityPercentage: item.opportunityPercentage ?? computedShare,
    };
  });
};

const OpportunityInfoCard = ({ title }: OpportunityInfoCardProps) => {
  const { selectedCountry } = useCountry();
  const currencyCode = selectedCountry?.currency?.code ?? "";

  const {
    data: depositBalanceData,
    isLoading,
    error: depositBalanceApiError,
  } = useQuery<DashboardAccount[]>({
    queryKey: ["dashboardAccountsBalance", "DEPOSIT", currencyCode],
    queryFn: async (): Promise<DashboardAccount[]> => {
      const response = await gxDashboardAccountsBalanceApi({
        pageNo: 0,
        pageSize: 0,
        filterBy2List: [
          {
            filterKey: "TYPE",
            filterStringValue: "DEPOSIT",
          },
          {
            filterKey: "CURRENCY_CODE",
            filterStringValue: currencyCode,
          },
        ],
      });

      if (response.status === 1) {
        return response.data;
      }

      throw new Error(response.errorMessage);
    },
    enabled: !!currencyCode,
  });

  const opportunityAccount = useMemo(() => {
    const accounts = mapOpportunityAccounts(depositBalanceData);

    if (accounts.length === 0) return null;

    return accounts.reduce((top, current) =>
      current.opportunityPercentage > top.opportunityPercentage ? current : top,
    );
  }, [depositBalanceData]);

  if (depositBalanceApiError) {
    console.error(
      "OpportunityInfoCard: gxDashboardAccountsBalanceApi failed",
      depositBalanceApiError,
    );
  }

  const headline = opportunityAccount
    ? "Reserve account underused"
    : "No underused accounts found";

  const description = opportunityAccount
    ? `${opportunityAccount.accountName} ${opportunityAccount.maskedAccountNo} holds ${opportunityAccount.opportunityPercentage}% of cash earning no yield.`
    : "";

  return (
    <InfoCard
      title={title || "OPPORTUNITY"}
      color="#32B26C"
      headline={isLoading ? "Loading..." : headline}
      description={isLoading ? "" : description}
    />
  );
};

export default OpportunityInfoCard;
